// Finds the library exercise behind a free-text name ("KB swings", "Kniebeugen", "goblet squats w/ kettlebell").
// Used by imports (link, text, photo), manual add ("did you mean"), and the MCP coach.
// Steps: normalize both sides the same way, try an exact name or alias, then score by token overlap with
// fuzzy tokens (edit distance) and by edit distance on the whole string. No network, no dependencies.
import { EXERCISES } from './seed';
import type { Equipment, Exercise } from './types';

export interface MatchCandidate {
  exercise: Exercise;
  /** 0 to 1; 1 is an exact name or alias. */
  score: number;
}

export interface MatchResult {
  /** The best candidate when there is one worth showing (score at least MATCH_MIN_SCORE). */
  match?: Exercise;
  /** True when the match is safe to use without asking: exact, or a clear winner. */
  confident: boolean;
  /** Best first, at most 5, all at least MATCH_MIN_SCORE. */
  candidates: MatchCandidate[];
}

/** Below this a candidate is not shown at all. */
export const MATCH_MIN_SCORE = 0.45;
/** A non-exact match is confident from this score up, if it also leads the next one by MATCH_MIN_LEAD. */
export const MATCH_CONFIDENT_SCORE = 0.82;
export const MATCH_MIN_LEAD = 0.08;

/** Words that carry no meaning for matching, English and German. Checked before and after stemming. */
const FILLER = new Set([
  'a', 'an', 'the', 'and', 'with', 'w', 'of', 'on', 'for', 'to', 'in', 'my', 'some', 'x', 'exercise',
  'mit', 'und', 'der', 'die', 'das', 'den', 'dem', 'ein', 'eine', 'einer', 'am', 'im', 'auf', 'zur', 'zum', 'uebung',
  // Amount and unit words that come with pasted workouts ("4 sets of 8", "plank 60 sec", "50x Hampelmaenner")
  'rep', 'reps', 'set', 'sets', 'round', 'rounds', 'sec', 'secs', 'second', 'seconds', 'min', 'mins', 'minute', 'minutes',
  'kg', 'kgs', 'lb', 'lbs', 'pound', 'pounds', 'each', 'per', 'total',
  'wdh', 'wiederholung', 'wiederholungen', 'satz', 'saetze', 'runde', 'runden', 'sek', 'sekunden', 'minuten', 'mal', 'je', 'pro',
]);

/** Tokens that are amounts, not names: 3x10, x12, 20kg, 60s, 45sec, 2min, 10reps, 50x. */
const AMOUNT = /^(\d+x\d*|x\d+|\d+(kg|kgs|lb|lbs|s|sec|secs|min|mins|m|km|reps?|wdh|x|er))$/;

/** Abbreviations expanded before matching (whole tokens only). */
const EXPAND: Record<string, string> = {
  kb: 'kettlebell', kbs: 'kettlebell', db: 'dumbbell', dbs: 'dumbbell', bb: 'barbell', bw: 'bodyweight',
  kettlebells: 'kettlebell', dumbbells: 'dumbbell', kurzhantel: 'dumbbell', kurzhanteln: 'dumbbell', langhantel: 'barbell',
  kugelhantel: 'kettlebell',
};

/** Query words that name equipment, and which equipment they mean. */
const EQUIPMENT_WORDS: Record<string, Equipment> = Object.fromEntries(
  Object.entries({ kettlebell: 'kettlebell', dumbbell: 'dumbbell', barbell: 'barbell', cable: 'cable', machine: 'machine', band: 'band', banded: 'band' })
    .map(([word, eq]) => [stem(word), eq as Equipment]),
);

/** Lowercase, umlauts to ae/oe/ue, sharp s to ss, accents off, punctuation to spaces, abbreviations expanded. */
export function normalizeName(text: string): string {
  const folded = text
    .toLowerCase()
    .replace(/\u00e4/g, 'ae').replace(/\u00f6/g, 'oe').replace(/\u00fc/g, 'ue').replace(/\u00df/g, 'ss')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/['\u2019]/g, '')
    .replace(/w\//g, 'with ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  return folded.split(' ').filter(Boolean).map((t) => EXPAND[t] ?? t).join(' ');
}

/** A rough stem so plurals and German endings meet: swings/swing, lunges/lunge, Kniebeugen/Kniebeuge, ups/up. */
function stem(token: string): string {
  if (/^\d+$/.test(token)) return token;
  if (token.length > 4 && token.endsWith('en')) return token.slice(0, -2);
  if (token.length > 4 && /(ches|shes|sses|xes)$/.test(token)) return token.slice(0, -2);
  // Short words ending in "es" keep it, so a typo like "pres" stays close to "press".
  if (token.length <= 4 && token.endsWith('es')) return token;
  if (token.length >= 3 && token.endsWith('s') && !token.endsWith('ss')) return stem(token.slice(0, -1));
  if (token.length > 3 && token.endsWith('e')) return token.slice(0, -1);
  return token;
}

function tokens(normalized: string): string[] {
  return normalized
    .split(' ')
    .filter((t) => t && !FILLER.has(t) && !/^\d+$/.test(t) && !AMOUNT.test(t))
    .map(stem)
    .filter((t) => !FILLER.has(t));
}

/** Edit distance where swapping two neighboring letters counts as one edit (optimal string alignment). */
function distance(a: string, b: string): number {
  if (a === b) return 0;
  let before: number[] = [];
  let prev: number[] = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const cur: number[] = [i];
    for (let j = 1; j <= b.length; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      let d = Math.min((prev[j] ?? 0) + 1, (cur[j - 1] ?? 0) + 1, (prev[j - 1] ?? 0) + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d = Math.min(d, (before[j - 2] ?? 0) + 1);
      cur[j] = d;
    }
    before = prev;
    prev = cur;
  }
  return prev[b.length] ?? 0;
}

function similarity(a: string, b: string): number {
  const longest = Math.max(a.length, b.length);
  return longest === 0 ? 1 : 1 - distance(a, b) / longest;
}

/** How well two tokens match: 1 when equal, the edit similarity when close (typos), else 0. */
function tokenMatch(a: string, b: string): number {
  if (a === b) return 1;
  const short = Math.min(a.length, b.length);
  const long = Math.max(a.length, b.length);
  // Cheap gates first: short words must match exactly, and a typo keeps the length and one of the first two letters.
  if (short < 4 || short / long < 0.75 || (a[0] !== b[0] && a[1] !== b[1])) return 0;
  const s = similarity(a, b);
  return s >= 0.75 ? s : 0;
}

/** Dice coefficient over tokens, with fuzzy token equality. */
type TokenMatcher = (query: string, target: string) => number;

function tokenScore(query: string[], target: string[], match: TokenMatcher): number {
  if (!query.length || !target.length) return 0;
  const used = new Set<number>();
  let sum = 0;
  for (const q of query) {
    let best = 0;
    let bestIndex = -1;
    target.forEach((t, i) => {
      if (used.has(i)) return;
      const m = match(q, t);
      if (m > best) { best = m; bestIndex = i; }
    });
    if (bestIndex >= 0) { used.add(bestIndex); sum += best; }
  }
  return (2 * sum) / (query.length + target.length);
}

interface Form { tokens: string[]; compact: string; gearWords: Set<string> }
interface Entry { exercise: Exercise; gear: Set<Equipment>; forms: Form[] }
interface Index { entries: Entry[]; vocabulary: string[] }

const indexCache = new WeakMap<Exercise[], Index>();

function index(exercises: Exercise[]): Index {
  const cached = indexCache.get(exercises);
  if (cached) return cached;
  const entries: Entry[] = exercises.map((exercise) => ({
    exercise,
    gear: new Set<Equipment>([exercise.equipment, ...(exercise.extraEquipment ?? [])]),
    forms: [exercise.name, ...(exercise.aliases ?? [])].map((n) => {
      const t = tokens(normalizeName(n));
      return { tokens: t, compact: t.join(''), gearWords: new Set(t.filter((w) => EQUIPMENT_WORDS[w])) };
    }),
  }));
  const vocabulary = [...new Set(entries.flatMap((e) => e.forms.flatMap((f) => f.tokens)))];
  const built = { entries, vocabulary };
  indexCache.set(exercises, built);
  return built;
}

/** Fuzzy token matches for one query, worked out once against the whole vocabulary. */
function queryMatcher(queryTokens: string[], vocabulary: string[]): TokenMatcher {
  const fuzzy = new Map<string, Map<string, number>>();
  for (const q of new Set(queryTokens)) {
    const hits = new Map<string, number>();
    for (const v of vocabulary) {
      const m = q === v ? 0 : tokenMatch(q, v);
      if (m > 0) hits.set(v, m);
    }
    fuzzy.set(q, hits);
  }
  return (q, t) => (q === t ? 1 : fuzzy.get(q)?.get(t) ?? 0);
}

function scoreForm(q: string[], compact: string, form: Form, best: number, match: TokenMatcher): number {
  if (compact && compact === form.compact) return 1;
  const byTokens = tokenScore(q, form.tokens, match);
  // Compound spellings ("pullups", "lat pull downs") change the token count; for those compare the whole string.
  // Its edit similarity can never beat the length ratio, so skip it when that cannot win.
  if (q.length === form.tokens.length && q.length > 1) return byTokens;
  if (compact.slice(0, 3) !== form.compact.slice(0, 3)) return byTokens;
  const bound = Math.min(compact.length, form.compact.length) / Math.max(compact.length, form.compact.length, 1);
  if (bound * 0.95 <= Math.max(byTokens, best)) return byTokens;
  return Math.max(byTokens, similarity(compact, form.compact) * 0.95);
}

function scoreEntry(entry: Entry, queryTokens: string[], queryGear: string[], match: TokenMatcher): number {
  // An equipment word in the query that this exercise uses is already satisfied; one it does not use costs a little.
  const satisfied = queryGear.filter((t) => entry.gear.has(EQUIPMENT_WORDS[t] as Equipment));
  const otherGear = queryGear.length - satisfied.length;
  const reduced = satisfied.length ? queryTokens.filter((t) => !satisfied.includes(t)) : queryTokens;
  const reducedCompact = reduced.join('');
  const fullCompact = queryTokens.join('');
  let best = 0;
  for (const form of entry.forms) {
    // Keep the equipment word when this name itself contains it ("Kettlebell swing").
    const keep = satisfied.some((t) => form.gearWords.has(t));
    const score = keep ? scoreForm(queryTokens, fullCompact, form, best, match) : scoreForm(reduced, reducedCompact, form, best, match);
    if (score === 1) return otherGear ? 0.9 : 1;
    if (score > best) best = score;
  }
  return Math.max(0, best - 0.1 * otherGear);
}

/**
 * Matches a free-text exercise name against the library (default: the built-in catalog).
 * confident is true for an exact name or alias, or a score of at least MATCH_CONFIDENT_SCORE that leads the
 * runner-up by MATCH_MIN_LEAD. When not confident, show the candidates or ask the AI fallback (see below).
 */
export function matchExercise(name: string, exercises: Exercise[] = EXERCISES): MatchResult {
  const queryTokens = tokens(normalizeName(name));
  if (!queryTokens.length) return { confident: false, candidates: [] };
  const queryGear = queryTokens.filter((t) => EQUIPMENT_WORDS[t]);
  const { entries, vocabulary } = index(exercises);
  const match = queryMatcher(queryTokens, vocabulary);
  const scored = entries
    .map((entry) => ({ exercise: entry.exercise, score: Math.round(scoreEntry(entry, queryTokens, queryGear, match) * 1000) / 1000 }))
    .filter((c) => c.score >= MATCH_MIN_SCORE)
    .sort((a, b) => b.score - a.score);
  const candidates = scored.slice(0, 5);
  const [first, second] = candidates;
  if (!first) return { confident: false, candidates };
  const lead = first.score - (second?.score ?? 0);
  const confident = (first.score === 1 && (second?.score ?? 0) < 1) || (first.score >= MATCH_CONFIDENT_SCORE && lead >= MATCH_MIN_LEAD);
  return { match: first.exercise, confident, candidates };
}

/**
 * Hook for a later step: when the matcher is not confident, an AI model may pick among the candidates or
 * propose a new exercise. The import Worker and the MCP coach pass their own implementation; the matcher stays
 * offline. Return the chosen exercise id, or null to keep the decision with the user.
 */
export type MatchFallback = (name: string, candidates: MatchCandidate[]) => Promise<string | null>;

/** matchExercise, then the fallback when not confident. A fallback answer outside the library is ignored. */
export async function matchExerciseWithFallback(name: string, fallback: MatchFallback, exercises: Exercise[] = EXERCISES): Promise<MatchResult & { by: 'matcher' | 'fallback' | 'none' }> {
  const result = matchExercise(name, exercises);
  if (result.confident) return { ...result, by: 'matcher' };
  const id = await fallback(name, result.candidates);
  const chosen = id ? exercises.find((e) => e.id === id) : undefined;
  return chosen ? { ...result, match: chosen, confident: true, by: 'fallback' } : { ...result, by: 'none' };
}
